import { FC } from 'react';
import { useForm } from 'react-final-form';

import { Question } from '~src/interfaces/question.interface';
import { ContentLocalesI } from '~src/interfaces/session.types';
import { Topic } from '~src/interfaces/topic.interface';
import ListAddForm from '~src/modules/surveys/components/topic_select/ListAddForm';
import { QuestionSubmit } from '~src/modules/topics/actions/topicActions';
import { ApiReduxResp, ThunkAnyAction } from '~src/services/apiMiddleware';
import { getTranslateKey } from '~src/services/translationServices';

import TopicSection from './TopicSection';
import { InnerAddWrapper } from './topicQuestions.sc';

interface Props {
  contentLocale: string;
  topic: Topic;
  selectedQuestions: Record<string, boolean>;
  mandatoryQuestions: string[];
  selectAll: (topic: Topic, value: boolean) => Promise<void>;
  defaultContentLocale: string;
  changeShowSave: (value: boolean) => void;
  addQuestion: (topicUuid: string, value: QuestionSubmit) => ApiReduxResp<Question>;
  editQuestion: (questionId: string, payload: QuestionSubmit) => ApiReduxResp<Question>;
  deleteQuestion: (topicId: string, questionId: string, name: ContentLocalesI | null) => ApiReduxResp<void>;
  deleteField: (field: string) => void;
}

const CustomQuestions: FC<Props> = (props) => {
  const renderAddForm = () => (
    <InnerAddWrapper $empty={false}>
      <ListAddForm
        inputLabelKey="x.survey.details.topic.form.add.question.label"
        addTextKey="x.survey.details.topic.form.add.question.button"
        submitForm={onAdd}
      />
    </InnerAddWrapper>
  );

  const form = useForm();

  function onAdd(value: string) {
    return new Promise((res, rej) => {
      props
        .addQuestion(props.topic.id, {
          title: { [props.defaultContentLocale]: value },
          selfTitle: { [props.defaultContentLocale]: value },
        })
        .then((resp) => {
          if (resp.response) {
            form.change(`questions.${resp.topicId}.${resp.response.data.id}`, false);
            res(resp);
          } else {
            rej(resp);
          }
        })
        .catch((err) => {
          rej(err);
        });
    });
  }

  function onEdit(value: string | null, question: Question): Promise<ThunkAnyAction<Question>> {
    const questionLocale = getTranslateKey(question.title);
    const oldTitle = { ...question.title };
    if (value && questionLocale) {
      oldTitle[questionLocale] = value;
    }
    return new Promise((res, rej) => {
      props
        .editQuestion(question.id, {
          title: oldTitle,
          selfTitle: oldTitle,
        })
        .then((resp) => {
          if (resp.response) {
            form.change(`questions.${resp.topicId}.${resp.response.data.id}`, false);
            res(resp);
          } else {
            rej(resp);
          }
        })
        .catch((err) => {
          rej(err);
        });
    });
  }

  function onDelete(questionId: string, name: ContentLocalesI | null): ApiReduxResp<void> {
    return new Promise((res, rej) => {
      props
        .deleteQuestion(props.topic.id, questionId, name)
        .then((resp) => {
          if (resp.response) {
            props.deleteField(`questions.${props.topic.id}.${questionId}`);
            res(resp);
          } else {
            rej(resp);
          }
        })
        .catch((err) => {
          rej(err);
        });
    });
  }

  return (
    <TopicSection
      changeShowSave={props.changeShowSave}
      topic={props.topic}
      selectAll={props.selectAll}
      selectedQuestions={props.selectedQuestions}
      mandatoryQuestions={props.mandatoryQuestions}
      defaultContentLocale={props.defaultContentLocale}
      isCreated={false}
      renderAddForm={renderAddForm}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
};

export default CustomQuestions;
